import React, { useState, useCallback, useMemo, lazy, Suspense } from 'react';
import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc } from 'firebase/firestore';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  ImageList,
  ImageListItem,
  Grid,
  Alert,
  Snackbar,
  Link,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { ImageUpload } from '../components/ImageUpload';
import { Logo } from '../components/Logo';
import { Check } from 'lucide-react';

const PrivacyDialog = lazy(() => import('../components/PrivacyDialog'));

function UploadPage() {
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    location: '',
    venue: '',
    rating: '',
  });
  const [currentFile, setCurrentFile] = useState(null);
  const [images, setImages] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'rating') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 10) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  }, []);

  const handleImageSelect = useCallback((file) => {
    setCurrentFile(file);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!currentFile) {
      setSnackbar({
        open: true,
        message: 'Please select an image to upload',
        severity: 'error',
      });
      return;
    }

    // Validate file type
    if (!currentFile.type.match('image/(jpeg|png|gif)')) {
      setSnackbar({
        open: true,
        message: 'Please upload only JPG, PNG, or GIF images',
        severity: 'error',
      });
      return;
    }

    // Validate file size (5MB max)
    if (currentFile.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: 'Image size must be less than 5MB',
        severity: 'error',
      });
      return;
    }

    // Validate form data
    if (!formData.firstName || !formData.lastName || !formData.location || !formData.venue || !formData.rating) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }

    setUploading(true);

    try {
      // Prepare the file data first
      const timestamp = Date.now();
      const sanitizedVenue = formData.venue.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const fileName = `${timestamp}_${formData.firstName.toLowerCase()}_${formData.lastName.toLowerCase()}_${sanitizedVenue}${getFileExtension(currentFile.name)}`;
      const storagePath = `uploads/${fileName}`;
      
      // Start both upload operations concurrently
      const [snapshot] = await Promise.all([
        uploadBytes(ref(storage, storagePath), currentFile, {
          contentType: currentFile.type,
          customMetadata: {
            uploadedBy: `${formData.firstName} ${formData.lastName}`,
            venue: formData.venue,
            location: formData.location
          }
        })
      ]);

      // Get the URL after upload is complete
      const url = await getDownloadURL(snapshot.ref);
      
      // Store metadata in Firestore
      const docRef = await addDoc(collection(db, 'user-posts'), {
        url,
        storagePath,
        fileName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        location: formData.location,
        venue: formData.venue,
        rating: parseFloat(formData.rating),
        createdAt: new Date(),
        originalFileName: currentFile.name,
        fileType: currentFile.type,
        fileSize: currentFile.size,
      });

      setImages(prev => [...prev, { 
        id: docRef.id,
        url,
        ...formData,
        rating: parseFloat(formData.rating)
      }]);

      // Show success dialog
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);

      setSnackbar({
        open: true,
        message: 'Image uploaded successfully!',
        severity: 'success',
      });

      // Reset form
      setCurrentFile(null);
      setFormData({
        firstName: '',
        lastName: '',
        location: '',
        venue: '',
        rating: '',
      });

    } catch (error) {
      console.error('Upload error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error uploading image. Please try again.',
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  }, [currentFile, formData]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Memoize the form fields configuration
  const formFields = useMemo(() => [
    {
      name: 'firstName',
      label: 'First Name',
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'lastName',
      label: 'Last Name',
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'location',
      label: 'Location',
      placeholder: 'e.g., New York, NY',
      gridProps: { xs: 12 }
    },
    {
      name: 'venue',
      label: 'Venue',
      placeholder: 'e.g., The Cocktail Bar',
      gridProps: { xs: 12 }
    }
  ], []);

  // Helper function to get file extension
  const getFileExtension = (filename) => {
    const ext = filename.split('.').pop();
    return ext ? `.${ext.toLowerCase()}` : '';
  };

  return (
    <Container maxWidth="sm" sx={{ position: 'relative' }}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          py: 4,
          position: 'relative',
          zIndex: 1,
          mt: 8,
          pt: '15vh'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          mb: 4
        }}>
          <Logo width={160} />
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Submit Your Groni's
          </Typography>
          <Typography 
            variant="body2" 
            align="center" 
            sx={{ 
              color: 'text.secondary',
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }
            }}
          >
            Look for your gronis on{' '}
            <Link 
              href="https://www.instagram.com/drinkgroni/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              @drinkgroni
            </Link>
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <ImageUpload onUpload={handleImageSelect} />
          
          <Grid container spacing={2}>
            {formFields.map(field => (
              <Grid item key={field.name} {...field.gridProps}>
                <TextField
                  fullWidth
                  label={field.label}
                  name={field.name}
                  variant="outlined"
                  value={formData[field.name]}
                  onChange={handleChange}
                  required
                  placeholder={field.placeholder}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rating (0-10)"
                name="rating"
                type="number"
                variant="outlined"
                value={formData.rating}
                onChange={handleChange}
                required
                inputProps={{
                  step: 0.1,
                  min: 0,
                  max: 10,
                }}
                helperText="Enter a rating between 0 and 10 (one decimal place)"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={uploading}
                sx={{ mt: 2 }}
              >
                {uploading ? <CircularProgress size={24} /> : 'Upload Image'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <ImageList cols={2} gap={16} sx={{ mt: 4 }}>
          {images.map((img) => (
            <ImageListItem key={img.id}>
              <img
                src={img.url}
                alt="Uploaded content"
                loading="lazy"
                style={{ borderRadius: 8 }}
              />
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2">
                  {img.venue} • {img.location}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rating: {parseFloat(img.rating).toFixed(1)}/10
                </Typography>
              </Box>
            </ImageListItem>
          ))}
        </ImageList>

        <Typography 
          variant="caption" 
          color="text.secondary" 
          align="center"
          sx={{ 
            mt: 'auto',
            pt: 4,
            fontSize: '0.7rem'
          }}
        >
          Your data will be processed in accordance with GDPR requirements. You can request access, modification, or deletion of your data at any time.
        </Typography>

        <Typography 
          variant="caption" 
          color="text.secondary" 
          align="center"
          sx={{ 
            mt: 1,
            mb: 2,
            fontSize: '0.7rem'
          }}
        >
          By submitting this form, you agree to our{' '}
          <Link
            component="button"
            variant="caption"
            onClick={() => setPrivacyDialogOpen(true)}
            sx={{
              fontSize: 'inherit',
              verticalAlign: 'baseline',
              textDecoration: 'underline'
            }}
          >
            Privacy Policy
          </Link>
        </Typography>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              overflow: 'hidden'
            }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', p: 3 }}>
            <Box
              sx={{
                backgroundColor: 'success.main',
                borderRadius: '50%',
                width: 80,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}
            >
              <Check size={40} color="white" />
            </Box>
            <Typography variant="h6" sx={{ color: 'white', mt: 2 }}>
              Upload Successful!
            </Typography>
          </DialogContent>
        </Dialog>

        <Suspense fallback={<CircularProgress />}>
          <PrivacyDialog 
            open={privacyDialogOpen} 
            onClose={() => setPrivacyDialogOpen(false)} 
          />
        </Suspense>
      </Box>
    </Container>
  );
}

export default UploadPage; 